import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { type FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { useGetOrderMaterialsQuery, useGetOrdersQuery } from '#api-bridge/orderApi.ts';
import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import type { Material } from '#domain/Material.ts';
import { isServiceOrder, isTransportOrder, type OrderType, type TransportOrderSummary } from '#domain/Order.ts';

import { CreateOrderButtonToolbar } from './CreateOrderButtonToolbar.tsx';
import { EditOrderButtonToolbar } from './EditOrderDialogFooter.tsx';
import { createOrderDuplicate, initialFormData, mapOrderToFormData } from './FormData.ts';
import { OrderForm } from './OrderForm.tsx';
import { type OrderFormData, OrderFormWizard } from './OrderFormValidation.ts';
import { ServiceOrderForm } from './ServiceOrderForm.tsx';

export type OrderDialogState = 'hidden' | 'createOrder' | 'editOrder' | 'duplicateOrder';

type CreateOrderDialogProps = {
    state: OrderDialogState;
    createType?: OrderType;
    selectedOrderId?: string;
    onClose: () => void;
    setActiveOrder: (orderId: string) => void;
};

const initialFormDataForType = (
    state: OrderDialogState,
    order: TransportOrderSummary,
    materials: Material[]
): OrderFormData => {
    switch (state) {
        case 'duplicateOrder':
            return createOrderDuplicate(order, materials);
        case 'editOrder':
            return mapOrderToFormData(order, materials);
        default:
            return initialFormData;
    }
};

export const OrderDialog = ({ state, createType = 'transport', onClose, setActiveOrder }: CreateOrderDialogProps) => {
    const intl = useIntl();
    const showServiceOrders = useFeatureToggle('showServiceOrders', false);
    const { orderId } = useParams();

    const orderTypes: OrderType[] = showServiceOrders ? ['transport', 'service'] : ['transport'];
    const { data: orderList } = useGetOrdersQuery(undefined, orderTypes);

    const orderById = orderList ? orderList.find(item => item.id === orderId) : undefined;

    const { data: materials } = useGetOrderMaterialsQuery(orderId ?? '', {
        skip: !orderId,
    });

    const formContext = useForm<OrderFormData>({
        resolver: zodResolver(OrderFormWizard),
        mode: 'onTouched',
        values:
            orderById && isTransportOrder(orderById)
                ? initialFormDataForType(state, orderById, materials ?? [])
                : initialFormData,
    });

    const { control, reset, setValue, watch } = formContext;
    const currentFormPage = watch('page');

    const onValidationErrors = (errors: FieldErrors<OrderFormData>) => {
        if (errors.loadingStop) {
            return showOrderFormLoadingStop();
        }
        if (errors.unloadingStop) {
            return showOrderFormUnloadingStop();
        }
        if (errors.materials) {
            return showFreightForm();
        }
    };

    const showFreightForm = () => {
        setValue('page', 'orderMaterial');
    };

    const showOrderFormLoadingStop = () => {
        setValue('page', 'loadingStop');
    };

    const showOrderFormUnloadingStop = () => {
        setValue('page', 'unloadingStop');
    };

    const handleClose = () => {
        reset({
            ...initialFormData,
            materials: [],
        });
        onClose();
        showOrderFormLoadingStop();
    };

    const showServiceForm =
        showServiceOrders &&
        ((state === 'createOrder' && createType === 'service') ||
            (state !== 'createOrder' && !!orderById && isServiceOrder(orderById)));
    const dialogAction = state === 'editOrder' ? 'edit' : 'create';
    const dialogType = showServiceForm ? '.service' : '';
    const dialogTitleId = `intl-msg:ordercom-cloud-frontend.orders.${dialogAction}-dialog${dialogType}.title`;
    const dialogTitle = intl.formatMessage({ id: dialogTitleId });

    return (
        <FormProvider {...formContext}>
            <Dialog
                show={state !== 'hidden'}
                onClose={handleClose}
                showCloseButton={true}
                bsSize='md'
                data-testid={`order-dialog-${state}`}
                headerButtons={
                    <Button
                        bsStyle={Button.MUTED}
                        iconOnly
                        iconName='rioglyph-remove'
                        autoTrackingKey={`${dialogTitleId}.close`}
                        className='modal-header-close close'
                        onClick={handleClose}
                        aria-label='dialog close button'
                    />
                }
            >
                <Dialog.Title title={dialogTitle} />
                <Dialog.Body className={'padding-0'}>
                    {showServiceForm ? (
                        <ServiceOrderForm
                            state={state}
                            order={orderById && isServiceOrder(orderById) ? orderById : undefined}
                            onClose={handleClose}
                            setActiveOrder={setActiveOrder}
                        />
                    ) : (
                        <fieldset>
                            <OrderForm
                                control={control}
                                page={currentFormPage}
                                state={state}
                                showLoadingStop={showOrderFormLoadingStop}
                                showUnloadingStop={showOrderFormUnloadingStop}
                                showOrderMaterial={showFreightForm}
                            />
                        </fieldset>
                    )}
                </Dialog.Body>

                {!showServiceForm && (
                    <Dialog.Footer>
                        {state === 'editOrder' && orderId && (
                            <EditOrderButtonToolbar
                                orderId={orderId}
                                handleClose={handleClose}
                                onValidationErrors={onValidationErrors}
                            />
                        )}
                        {['createOrder', 'duplicateOrder'].includes(state) && (
                            <CreateOrderButtonToolbar
                                currentFormPage={currentFormPage}
                                setActiveOrder={setActiveOrder}
                                showMaterial={showFreightForm}
                                showLoadingStop={showOrderFormLoadingStop}
                                showUnloadingStop={showOrderFormUnloadingStop}
                                onValidationErrors={onValidationErrors}
                                handleClose={handleClose}
                            />
                        )}
                    </Dialog.Footer>
                )}
            </Dialog>
        </FormProvider>
    );
};
